import { addDays, format, isSameDay } from 'date-fns';
import { TFunction } from 'i18next';

// Below this margin the scheduled option is not offered: the backend needs at least 60 seconds
// and the paid period is about to expire anyway.
const ACTIONABLE_MARGIN_MS = 15 * 60 * 1000;

function parseIso(iso?: string | null): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Formats a PPU coverage boundary for the copy keys that take `{{time}}` and `{{day}}`.
 * `time` is "HH:mm" in the browser timezone; `day` is "today", "tomorrow" or a short date,
 * prefixed with "on" in English and "del" in Italian (where today/tomorrow carry "di") so that "{{time}} {{day}}" reads naturally.
 */
export function formatCoveredUntil(
  iso: string,
  t: TFunction,
  lang: string,
  now: Date = new Date()
): { time: string; day: string } {
  const date = new Date(iso);
  const time = format(date, 'HH:mm');

  if (isSameDay(date, now)) return { time, day: t('day_today') };
  if (isSameDay(date, addDays(now, 1))) return { time, day: t('day_tomorrow') };

  const shortDate = format(date, 'dd/MM');
  return { time, day: lang.startsWith('en') ? `on ${shortDate}` : `del ${shortDate}` };
}

/** True when the ISO date is valid and more than 15 minutes in the future. */
export function isCoveredUntilActionable(iso?: string | null, now: number = Date.now()): boolean {
  const date = parseIso(iso);
  return !!date && date.getTime() > now + ACTIONABLE_MARGIN_MS;
}
